import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Download, Pause, PlayFill, VolumeOff, VolumeUp } from 'react-bootstrap-icons';

import { Button, Popover, Slider, Spin, Typography } from 'antd';

import { openInNewTab, secondsToTimeString } from '@/helpers';
import styled, { css, StyledProp } from '@/styled';

enum enStatus {
  'PAUSED' = 'paused',
  'PLAYING' = 'playing',
  'LOADING' = 'loading'
}

interface IProps {
  src: string;
  enabledDownload: boolean;
}

const AudioPlayer = (props: IProps & StyledProp) => {
  const { src, enabledDownload } = props;
  const audio = useRef<HTMLAudioElement>(new Audio(src));

  const [status, setStatus] = useState(enStatus.LOADING);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [volume, setVolume] = useState(100);
  const [isLoaded, setIsLoaded] = useState(false);

  const pause = useCallback(() => {
    audio.current.pause();
    setStatus(enStatus.PAUSED);
  }, []);

  const onAudioEnded = useCallback(() => {
    pause();
    audio.current.currentTime = 0;
    setCurrentTime(0);
  }, [audio, pause]);

  const canPlayThrough = useCallback(() => {
    setDuration(Math.floor(audio.current.duration));
    if (!isLoaded) setIsLoaded(true);

    if (!audio.current.duration || audio.current.paused) {
      setStatus(enStatus.PAUSED);
      return;
    }

    setStatus(enStatus.PLAYING);
  }, [isLoaded]);

  const onTimeUpdated = () => {
    setCurrentTime(Math.floor(audio.current.currentTime));
  };
  const play = useCallback(() => {
    audio.current.play().then(() => setStatus(enStatus.PLAYING));
  }, []);

  const enableVolume = useCallback(() => {
    if (audio.current.volume > 0) {
      audio.current.volume = 0;
      setVolume(0);
      return;
    }

    setVolume(100);
    audio.current.volume = 1;
  }, []);

  const handleChangeCurrentTime = useCallback((newValue: number) => {
    setStatus(enStatus.LOADING);
    setCurrentTime(Math.floor(newValue));
    audio.current.currentTime = newValue;
  }, []);

  const handleChangeVolume = useCallback((newValue: number) => {
    audio.current.volume = newValue / 100;
    setVolume(newValue);
  }, []);

  useEffect(() => {
    const audioReference = audio.current;

    audioReference.addEventListener('canplaythrough', canPlayThrough, false);
    audioReference.addEventListener('timeupdate', onTimeUpdated, false);
    audioReference.addEventListener('ended', onAudioEnded, false);

    return () => {
      onAudioEnded();
      audioReference.removeEventListener('timeupdate', onTimeUpdated);
      audioReference.removeEventListener('ended', onAudioEnded);
      audioReference.removeEventListener('canplaythrough', canPlayThrough);
    };
  }, [audio, canPlayThrough, onAudioEnded]);

  const button: Record<enStatus, JSX.Element> = {
    [enStatus.PAUSED]: <Button type='ghost' icon={<PlayFill size={24} />} onClick={play} />,
    [enStatus.PLAYING]: <Button type='ghost' icon={<Pause size={24} />} onClick={pause} />,
    [enStatus.LOADING]: <Spin />
  };

  if (!src) return null;

  return (
    <div className={props.className}>
      {button[status] ?? button[enStatus.LOADING]}

      <Typography.Paragraph>
        {secondsToTimeString(currentTime, duration >= 3600)}&nbsp;/&nbsp;{secondsToTimeString(duration)}
      </Typography.Paragraph>

      <Slider disabled={!isLoaded} onChange={handleChangeCurrentTime} value={currentTime} max={duration} min={0} />
      <Popover title={<Slider min={0} max={100} onChange={handleChangeVolume} value={volume} />}>
        <Button
          type='ghost'
          icon={!!volume ? <VolumeUp size={24} /> : <VolumeOff size={24} />}
          onClick={enableVolume}
        />
      </Popover>

      {enabledDownload && <Button type='ghost' icon={<Download size={24} />} onClick={() => openInNewTab(src)} />}
    </div>
  );
};

export default React.memo(
  styled(AudioPlayer)(
    ({ theme }) => css`
      display: flex;
      flex-direction: row;
      gap: ${theme.space.pxToRem(16)};
      background: ${theme.variables.audioPlayerBackground};
      width: 100%;
      align-items: center;
      justify-content: center;
      padding: ${theme.space.pxToRem(16)};
      border-radius: ${theme.borderRadius}px;

      .ant-typography {
        width: 160px;
        margin: 0;
      }

      .ant-spin {
        width: 20px;
        height: 20px;
      }

      .ant-slider-rail {
        background: ${theme.palette.gray[2]} !important;
      }

      .ant-slider-handle::after {
        background: ${theme.palette.gray[9]};
      }

      svg {
        color: ${theme.colorText};
      }

      .ant-slider {
        width: 100%;
      }
    `
  )
);
